// hooks/useOperationalStream.ts
// ────────────────────────────────────────────────────────────────────────────
// Hybrid operational live-state loader for TableOps.
//
// Primary:  HTTP fetch (always works, immediate) → /operations/snapshot
// Live:     SSE EventSource for background updates → /operations/stream
//
// On mount:   HTTP fetch the full reconstructed operational state immediately.
// After edit: nudge() → HTTP refetch immediately + reconnect SSE.
// Background: SSE pushes a fresh snapshot whenever the server-reconstructed
//             state changes (zero polling noise on the client).
//
// The server is the source of truth. Every connected client converges on the
// same reconstructed state with deterministic ids, so concurrent operators on
// the same tenant stay in sync without refreshing the page.
// ────────────────────────────────────────────────────────────────────────────
import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { fetchOperationsSnapshot, type OperationsSnapshot } from '../api/tableOpsApi'
import { useTenantHybridStreamCore } from './useTenantHybridStreamCore'

export interface UseOperationalStreamResult {
  /** Latest server-reconstructed operational state (null until first load). */
  snapshot: OperationsSnapshot | null
  /** Whether the snapshot has been loaded at least once. */
  loaded: boolean
  /** Whether the SSE connection is currently open. */
  connected: boolean
  /** Non-null if SSE is disconnected or errored. */
  error: string | null
  /** Immediately refetch via HTTP + reconnect SSE (use after local mutations). */
  nudge: () => void
}

export function useOperationalStream(tenantId?: number | string): UseOperationalStreamResult {
  const [snapshot, setSnapshot] = useState<OperationsSnapshot | null>(null)
  const latestGeneratedMsRef = useRef<number>(0)
  const latestSeqRef = useRef<number>(0)
  const previousTenantKeyRef = useRef<string>('')

  const tenantKey = String(tenantId ?? '')

  useLayoutEffect(() => {
    if (previousTenantKeyRef.current === tenantKey) return
    previousTenantKeyRef.current = tenantKey
    latestGeneratedMsRef.current = 0
    latestSeqRef.current = 0
    setSnapshot(null)
  }, [tenantKey])

  const tryApplySnapshot = useCallback((next: OperationsSnapshot, opts?: { seq?: number }) => {
    const nextGeneratedMs = Date.parse(next.generated_at || '')
    const normalizedGeneratedMs = Number.isFinite(nextGeneratedMs) ? nextGeneratedMs : 0
    const nextSeq = opts?.seq

    // Drop out-of-order payloads (HTTP poll / reconnect race) so older
    // snapshots never rewind visible state after a fresh action.
    if (Number.isFinite(nextSeq) && (nextSeq as number) < latestSeqRef.current) return
    if (normalizedGeneratedMs < latestGeneratedMsRef.current) return

    if (Number.isFinite(nextSeq)) latestSeqRef.current = nextSeq as number
    if (normalizedGeneratedMs > 0) latestGeneratedMsRef.current = normalizedGeneratedMs
    setSnapshot(next)
  }, [])

  const { loaded, connected, error, nudge } = useTenantHybridStreamCore<
    OperationsSnapshot,
    { seq?: number; snapshot: OperationsSnapshot }
  >({
    tenantId,
    ssePath: '/bff/table-ops/operations/stream',
    fetchSnapshot: () => fetchOperationsSnapshot(),
    onHttpSnapshot: (next) => {
      tryApplySnapshot(next)
    },
    onSseSnapshot: ({ seq, snapshot: next }) => {
      tryApplySnapshot(next, { seq })
    },
    parseSseSnapshot: (raw) => {
      const payload = raw as { seq?: number } & OperationsSnapshot
      const { seq, ...rest } = payload
      return { ok: true, value: { seq, snapshot: rest as OperationsSnapshot } }
    },
  })

  return { snapshot, loaded, connected, error, nudge }
}
